import { RTL_LANGUAGES, SUPPRESS_US_COOKIE_BANNER_COUNTRIES } from '@constants';
import { SecurityContent } from './use-security-content';

// Markets with no authored AEM "security" content fragment yet; fall back to a static mock instead of a blank page.
export const FALLBACK_SECURITY_CONTENT_LOCALES = [
    ...SUPPRESS_US_COOKIE_BANNER_COUNTRIES,
    ...RTL_LANGUAGES,
];

interface FallbackText {
    en: string;
    ar: string;
}

// Single source of truth for both locales, keyed once to avoid duplicating the SecurityContent shape per language.
const FALLBACK_SECURITY_CONTENT_TEXT: Record<
    keyof SecurityContent,
    FallbackText
> = {
    returnButtonText: { en: 'Account', ar: 'الحساب' },
    brandLogo: { en: '/', ar: '/' },
    signInCredentialsSectionHeader: {
        en: 'Sign In Credentials',
        ar: 'بيانات تسجيل الدخول',
    },
    emailSectionLabel: { en: 'Email', ar: 'البريد الإلكتروني' },
    passwordSectionLabel: { en: 'Password', ar: 'كلمة المرور' }, // NOSONAR content-fragment label, not a credential
    passkeysSectionHeader: { en: 'Passkeys', ar: 'مفاتيح المرور' },
    passkeysIconSubheadingOne: {
        en: 'Strengthen your account',
        ar: 'عزّز حماية حسابك',
    },
    passkeysIconSubheadingTwo: {
        en: 'Skip the password',
        ar: 'تجاوز كلمة المرور',
    },
    passkeysIconSubheadingThree: { en: 'Save time', ar: 'وفّر الوقت' },
    passkeysPreDescription: {
        en: 'A passkey is a unique key kept on your device that lets you skip the complex password. Create one with your face, fingerprint, or PIN. Ford won’t see your biometric data or PIN.',
        ar: 'مفتاح المرور هو مفتاح فريد يتم الاحتفاظ به على جهازك يتيح لك تخطي كلمة المرور المعقدة. أنشئ مفتاحًا باستخدام وجهك أو بصمتك أو رمز PIN. لن تطلع فورد على بياناتك الحيوية أو رمز PIN.',
    },
    passkeysPostDescription: { en: '', ar: '' },
    passkeysIdText: { en: 'ID', ar: 'المعرّف' },
    passkeysCreatedText: { en: 'Created', ar: 'تاريخ الإنشاء' },
    passkeysCreateButton: { en: 'Create Passkey', ar: 'إنشاء مفتاح مرور' },
    passkeysRemoveButton: { en: 'Remove Passkey', ar: 'إزالة مفتاح المرور' },
    passkeysCreationSuccessNotificationHeader: {
        en: 'You have successfully created your passkey',
        ar: 'لقد أنشأت مفتاح المرور بنجاح',
    },
    passkeysCreationSuccessNotificationDescription: { en: '', ar: '' },
    passkeysRemoveModalHeader: {
        en: 'Remove Passkey',
        ar: 'إزالة مفتاح المرور',
    },
    passkeysRemoveModalDescription: {
        en: 'Are you sure you want to remove this passkey?',
        ar: 'هل أنت متأكد من رغبتك في إزالة مفتاح المرور هذا؟',
    },
    cancelButton: { en: 'Cancel', ar: 'إلغاء' },
    passkeysRemovalSuccessNotificationHeader: {
        en: 'You have successfully removed your passkey',
        ar: 'لقد أزلت مفتاح المرور بنجاح',
    },
    passkeysRemovalSuccessNotificationDescription: { en: '', ar: '' },
    passkeysRemovalErrorNotificationHeader: {
        en: 'Removal failed',
        ar: 'فشلت عملية الإزالة',
    },
    passkeysRemovalErrorNotificationDescription: {
        en: 'Your passkey was not removed',
        ar: 'لم تتم إزالة مفتاح المرور الخاص بك',
    },
    MFASectionHeader: {
        en: 'Multi-Factor Authentication',
        ar: 'المصادقة متعددة العوامل',
    },
    MFADescription: {
        en: 'Select how you’ll get a temporary code when you sign in to Ford. Save time, without sacrificing security – skip these steps with passkey.',
        ar: 'حدد كيف ستتلقى رمزًا مؤقتًا عند تسجيل الدخول إلى فورد. وفّر الوقت دون التضحية بالأمان - تخطّ هذه الخطوات باستخدام مفتاح المرور.',
    },
    MFAToggleHeader: { en: '', ar: '' },
    MFAToggleOnLabelText: { en: '', ar: '' },
    MFAToggleOffLabelText: { en: '', ar: '' },
    MFAEnableModalHeaderText: {
        en: 'Setup multi-factor authentication',
        ar: 'إعداد المصادقة متعددة العوامل',
    },
    MFAEnableModalBodyText: {
        en: 'We need to confirm your identity',
        ar: 'نحتاج إلى تأكيد هويتك',
    },
    MFAEnableModalConfirmButtonLabelText: {
        en: 'Sign Out',
        ar: 'تسجيل الخروج',
    },
    MFAEnableModalConfirmButtonAriaLabel: {
        en: 'Sign Out and enable multi-factor authentication',
        ar: 'تسجيل الخروج وتفعيل المصادقة متعددة العوامل',
    },
    MFAEnableModalCancelButtonLabelText: { en: 'Cancel', ar: 'إلغاء' },
    MFAEnableModalCancelButtonAriaLabel: {
        en: 'Cancel and close modal',
        ar: 'إلغاء وإغلاق النافذة',
    },
    MFAEnableModalCloseButtonAriaLabel: {
        en: 'Close the modal',
        ar: 'إغلاق النافذة',
    },
    MFADisableModalHeaderText: {
        en: 'Turn Off Multi-Factor Authentication',
        ar: 'إيقاف المصادقة متعددة العوامل',
    },
    MFADisableModalBodyText: {
        en: 'We need to make sure it’s you before we turn it off',
        ar: 'نحتاج إلى التأكد من هويتك قبل إيقافها',
    },
    MFADisableModalConfirmButtonLabelText: { en: 'Continue', ar: 'متابعة' },
    MFADisableModalConfirmButtonAriaLabel: {
        en: 'Continue and disable multi-factor authentication',
        ar: 'المتابعة وإيقاف المصادقة متعددة العوامل',
    },
    MFADisableModalCancelButtonLabelText: { en: 'Cancel', ar: 'إلغاء' },
    MFADisableModalCancelButtonAriaLabel: {
        en: 'Cancel and close modal',
        ar: 'إلغاء وإغلاق النافذة',
    },
    MFADisableModalCloseButtonAriaLabel: {
        en: 'Close the modal',
        ar: 'إغلاق النافذة',
    },
    MFAEmailAuthenticationSectionLabel: {
        en: 'Email Authentication',
        ar: 'المصادقة عبر البريد الإلكتروني',
    },
    MFAEmailAuthenticationDescriptionText: {
        en: 'Receive a one-time code by email to verify your identity.',
        ar: 'احصل على رمز لمرة واحدة عبر البريد الإلكتروني للتحقق من هويتك.',
    },
    MFAAppAuthenticationSectionLabel: {
        en: 'App Authentication',
        ar: 'المصادقة عبر التطبيق',
    },
    MFAAppAuthenticationDescriptionText: {
        en: 'Use a code generated by an authenticator app.',
        ar: 'استخدم رمزًا تم إنشاؤه بواسطة تطبيق مصادقة.',
    },
    MFASmsAuthenticationSectionLabel: {
        en: 'SMS Authentication',
        ar: 'المصادقة عبر الرسائل النصية',
    },
    MFASmsAuthenticationDescriptionText: {
        en: 'Use a code sent to your verified mobile number.',
        ar: 'استخدم رمزًا يتم إرساله إلى رقم هاتفك المحمول الموثق.',
    },
    editButtonText: { en: 'Edit', ar: 'تعديل' },
    addButtonText: { en: 'Add', ar: 'إضافة' },
    removeButtonText: { en: 'Remove', ar: 'إزالة' },
    mfaGetOptionsErrorNotificationMessage: {
        en: 'We’re unable to retrieve your multi-factor authentication settings. Please try again later.',
        ar: 'يتعذّر علينا استرداد إعدادات المصادقة متعددة العوامل الخاصة بك. يُرجى المحاولة مرة أخرى لاحقًا.',
    },
    mfaUpdateUserLevelFlagErrorNotificationMessage: {
        en: 'We’re unable to update your MFA configuration. Please try again later.',
        ar: 'يتعذّر علينا تحديث إعدادات المصادقة متعددة العوامل الخاصة بك. يُرجى المحاولة مرة أخرى لاحقًا.',
    },
    mfaDeleteOptionErrorNotificationMessage: {
        en: 'We’re unable to remove your multi-factor authentication option. Please try again later.',
        ar: 'يتعذّر علينا إزالة خيار المصادقة متعددة العوامل الخاص بك. يُرجى المحاولة مرة أخرى لاحقًا.',
    },
    errorPageReturnButtonText: {
        en: 'Sign In & Security',
        ar: 'تسجيل الدخول والأمان',
    },
    errorPageReturnButtonAriaLabel: {
        en: 'Return to Sign In & Security',
        ar: 'العودة إلى تسجيل الدخول والأمان',
    },
    errorPageAlertIcon: { en: '/image', ar: '/image' },
    errorPageHeaderText: { en: 'Remove Authentication', ar: 'إزالة المصادقة' },
    errorPageDescriptionText: {
        en: 'Something went wrong. Please try again later.',
        ar: 'حدث خطأ ما. يُرجى المحاولة مرة أخرى لاحقًا.',
    },
    errorPageCancelButtonText: { en: 'Cancel', ar: 'إلغاء' },
    errorPageCancelButtonAriaLabel: {
        en: 'Cancel and return to Sign In & Security',
        ar: 'إلغاء والعودة إلى تسجيل الدخول والأمان',
    },
    signInSecurityButtonText: {
        en: 'Sign In and Security',
        ar: 'تسجيل الدخول والأمان',
    },
    MFAEmailChangeWarningModalHeaderText: {
        en: 'Edit email for sign in?',
        ar: 'هل تريد تعديل البريد الإلكتروني لتسجيل الدخول؟',
    },
    MFAEmailChangeWarningModalBodyText: {
        en: 'Any update also changes the email address you use to sign in to your account',
        ar: 'أي تحديث سيغيّر أيضًا عنوان البريد الإلكتروني الذي تستخدمه لتسجيل الدخول إلى حسابك.',
    },
    MFAEmailChangeWarningModalContinueButton: { en: 'Continue', ar: 'متابعة' },
    MFAEmailChangeWarningModalContinueButtonAriaLabel: {
        en: 'Continue',
        ar: 'متابعة',
    },
    MFAEmailChangeWarningModalCancelButton: { en: 'Cancel', ar: 'إلغاء' },
    MFAEmailChangeWarningModalCancelButtonAriaLabel: {
        en: 'Cancel',
        ar: 'إلغاء',
    },
};

const buildFallbackContent = (locale: keyof FallbackText): SecurityContent =>
    Object.entries(FALLBACK_SECURITY_CONTENT_TEXT).reduce(
        (content, [key, text]) => ({ ...content, [key]: text[locale] }),
        {} as SecurityContent
    );

export const securityContentFallbackEn: SecurityContent =
    buildFallbackContent('en');
export const securityContentFallbackAr: SecurityContent =
    buildFallbackContent('ar');
